import React, { ReactNode, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Box, Collapse } from "@mui/material";
import { useNavigate } from "react-router";
import ArrowRightIcon from "@icons/myicons/arrow-right-filled.svg?react";
import { useAppLayout } from "@providers/AppLayoutProvider";

const Container = styled(Box)<{
  selected: boolean;
}>`
  text-decoration: none;
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px`};
  color: ${({
    theme: {
      palette: { sidebar },
    },
    selected,
  }) => (selected ? sidebar.sidebarColorOnDark : sidebar.textOnSidebar)};
`;

const ItemContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "selected" && prop !== "bigIcon" && prop !== "collapsed",
})<{
  selected: boolean;
  bigIcon?: boolean;
  collapsed?: boolean;
}>`
  text-decoration: none;
  display: flex;
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px`};
  background-color: ${({
    theme: {
      palette: { sidebar },
    },
    selected,
  }) => (selected ? sidebar.active : "transparent")};
  padding: ${({ theme, bigIcon, collapsed }) =>
    bigIcon && collapsed ? 0 : theme.spacing(1.5, 2)};
  justify-items: end;
  align-items: center;
  cursor: pointer;
  color: inherit;

  & svg {
    color: ${({
      theme: {
        palette: { sidebar },
      },
      selected,
    }) => (selected ? sidebar.sidebarColorOnDark : sidebar.textOnSidebar)};
  }

  &:hover {
    background-color: ${({
      theme: {
        palette: { sidebar },
      },
      selected,
      bigIcon,
      collapsed,
    }) =>
      bigIcon && collapsed
        ? "inherit"
        : selected
          ? sidebar.active
          : sidebar.hover};
    color: ${({
      theme: {
        palette: { sidebar },
      },
    }) => sidebar.sidebarColorOnDark};
    & svg {
      color: ${({
        theme: {
          palette: { sidebar },
        },
      }) => sidebar.sidebarColorOnDark};
    }
  }
`;

const ExpandIconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "expand" && prop !== "bigIcon",
})<{ expand: number; bigIcon?: boolean }>`
  display: flex;
  align-items: center;
  font-size: 16px;
  color: ${({
    theme: {
      palette: { sidebar },
    },
  }) => sidebar.inactive};

  & svg {
    transform: ${({ expand, bigIcon }) =>
      expand ? `rotate(${bigIcon ? "180deg" : "90deg"})` : "none"};
    transition: transform 0.1s;
  }
`;

const ChildrenContainer = styled(Box)(
  ({ theme }) => `
  max-height: 154px;
  overflow-y: auto;
  
  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${theme.palette.wizardHeader.header} ${theme.palette.background.sidebar};

  &::-webkit-scrollbar {
    width: 12px;
  }

  &::-webkit-scrollbar-track {
    background: 
      ${theme.palette.background.sidebar};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${theme.palette.wizardHeader.header};
    border-radius: 6px;
    border: 3px solid ${theme.palette.background.sidebar};
  }

  & > div {
    margin-top: ${theme.spacing(1)};
  }

  & .menuItem {
    padding-left: ${theme.spacing(5)};
  }
`
);

const MenuItemText = styled(Box)`
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
  margin-right: ${({ theme }) => theme.spacing(1)};
`;

export interface MenuItemExpandableProps {
  name: string | ReactNode;
  before?: ReactNode;
  expandIcon?: React.ReactNode;
  selected: boolean;
  children: ReactNode;
  to?: string;
  bigIcon?: boolean;
  onClick?: () => void;
}

export const MenuItemExpandable = ({
  name,
  selected,
  before,
  expandIcon = <ArrowRightIcon />,
  children,
  to,
  bigIcon,
  onClick,
}: MenuItemExpandableProps) => {
  const [expand, setExpand] = useState(selected);
  useEffect(() => {
    setExpand(selected);
  }, [selected]);

  const navigate = useNavigate();
  const { sidebarCollapsed: collapsed } = useAppLayout();

  const handleClick = () => {
    if (!collapsed) {
      setExpand(!expand);
    }
    onClick && onClick();
    if (to && !selected) return navigate(to);
  };

  return (
    <Container selected={selected} width="100%">
      <ItemContainer
        width="100%"
        onClick={handleClick}
        selected={selected}
        bigIcon={bigIcon}
        collapsed={collapsed}
        display="flex"
        justifyContent="space-between"
      >
        <Box alignItems="center" display="flex" minWidth="0" width="100%">
          <Box display="flex" mr={collapsed ? 0 : 4}>
            {before}
          </Box>
          {!collapsed && <MenuItemText>{name}</MenuItemText>}
        </Box>
        {!collapsed && (
          <ExpandIconContainer bigIcon={bigIcon} expand={expand ? 1 : 0}>
            {expandIcon}
          </ExpandIconContainer>
        )}
      </ItemContainer>
      {!collapsed && (
        <Collapse component={Box} in={expand}>
          <ChildrenContainer>{children}</ChildrenContainer>
        </Collapse>
      )}
    </Container>
  );
};
